import { useState } from 'react';
import { Input } from '@tkpod/ui';
import { HexColorPicker } from 'react-colorful';
import type { MetadataFieldDef } from '@tkpod/shared';
import { ImageUploadInput } from '@/components/ImageUploadInput';

interface DynamicMetadataFormProps {
  schema: MetadataFieldDef[];
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  disabled?: boolean;
}

export function DynamicMetadataForm({ schema, value, onChange, disabled }: DynamicMetadataFormProps) {
  const validFields = (schema ?? []).filter((f) => f.key && f.type);

  if (validFields.length === 0) {
    return (
      <p className="text-sm text-text-tertiary">
        No metadata fields configured for this option type.
      </p>
    );
  }

  const groups = groupFields(validFields);

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.name}>
          {group.name && (
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              {group.name}
            </p>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {group.fields.map((field) => (
              <div
                key={field.key}
                className={field.gridCols === 2 ? 'md:col-span-2' : ''}
              >
                <MetadataField
                  field={field}
                  fieldValue={getNestedValue(value, field.key)}
                  onChange={(v) => onChange(setNestedValue({ ...value }, field.key, v))}
                  disabled={disabled}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MetadataField({
  field,
  fieldValue,
  onChange,
  disabled,
}: {
  field: MetadataFieldDef;
  fieldValue: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}) {
  const label = field.label || field.key;

  switch (field.type) {
    case 'color':
      return (
        <ColorField
          label={label}
          required={field.required}
          value={(fieldValue as string) ?? (field.defaultValue as string) ?? ''}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'image':
      return (
        <ImageUploadInput
          label={`${label}${field.required ? ' *' : ''}`}
          value={(fieldValue as string) ?? ''}
          onChange={(url) => onChange(url || undefined)}
          directory="swatches"
          previewSize="sm"
          accept="image/png,image/jpeg,image/webp"
        />
      );

    case 'number':
      return (
        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary">
            {label}{field.required && ' *'}
          </label>
          <Input
            type="number"
            step={field.step ?? 'any'}
            min={field.min}
            max={field.max}
            value={fieldValue != null ? String(fieldValue) : ''}
            onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder={field.placeholder}
            disabled={disabled}
          />
        </div>
      );

    case 'boolean':
      return (
        <div className="flex items-center gap-3 pt-5">
          <button
            type="button"
            role="switch"
            aria-checked={!!fieldValue}
            onClick={() => !disabled && onChange(!fieldValue)}
            disabled={disabled}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              fieldValue ? 'bg-success' : 'bg-border-strong'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                fieldValue ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <label className="text-sm font-medium text-text-secondary">
            {label}{field.required && ' *'}
          </label>
        </div>
      );

    case 'select':
      return (
        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary">
            {label}{field.required && ' *'}
          </label>
          <select
            value={(fieldValue as string) ?? (field.defaultValue as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            disabled={disabled}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
          >
            <option value="">— Select —</option>
            {(field.options ?? []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );

    case 'string':
    default:
      return (
        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary">
            {label}{field.required && ' *'}
          </label>
          <Input
            value={(fieldValue as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            placeholder={field.placeholder}
            disabled={disabled}
          />
        </div>
      );
  }
}

function ColorField({
  label,
  required,
  value,
  onChange,
  disabled,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}) {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-text-secondary">
        {label}{required && ' *'}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-9 w-9 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0.5"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="font-mono text-sm"
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => setShowPicker(!showPicker)}
          disabled={disabled}
          className="shrink-0 rounded border border-border px-2 py-1.5 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-50"
        >
          {showPicker ? '▲' : '▼'}
        </button>
      </div>
      {showPicker && (
        <div className="mt-2">
          <HexColorPicker color={value || '#000000'} onChange={(c) => onChange(c)} />
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface FieldGroup {
  name: string;
  fields: MetadataFieldDef[];
}

function groupFields(schema: MetadataFieldDef[]): FieldGroup[] {
  const groups: FieldGroup[] = [];
  const seen = new Map<string, FieldGroup>();

  for (const field of schema) {
    const groupName = field.group ?? '';
    let group = seen.get(groupName);
    if (!group) {
      group = { name: groupName, fields: [] };
      seen.set(groupName, group);
      groups.push(group);
    }
    group.fields.push(field);
  }

  return groups;
}

function getNestedValue(obj: Record<string, unknown>, key: string): unknown {
  const parts = key.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function setNestedValue(obj: Record<string, unknown>, key: string, value: unknown): Record<string, unknown> {
  const parts = key.split('.');
  if (parts.length === 1) {
    if (value === undefined || value === '') {
      const copy = { ...obj };
      delete copy[key];
      return copy;
    }
    return { ...obj, [key]: value };
  }

  const [first, ...rest] = parts;
  const nested = (obj[first] as Record<string, unknown>) ?? {};
  const updatedNested = setNestedValue({ ...nested }, rest.join('.'), value);

  if (Object.keys(updatedNested).length === 0) {
    const copy = { ...obj };
    delete copy[first];
    return copy;
  }

  return { ...obj, [first]: updatedNested };
}
